import styled from "@emotion/styled";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import en from "lang/en.json";
import es from "lang/es.json";
import React from "react";
import Typography from "@components/Typography";
import HeadMeta, { IHeadMetaComponent } from "@components/head-meta";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from "@mui/material";
import { CFrequencyQuestions } from "shared/models/const/frequency-questions.const";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";

import type { GetStaticPropsContext, NextPage } from "next";
import WsSectionTitle from "@components/a1-refactor/typography/WsSectionTitle";
export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: locale === "es" ? es : en,
      lang: locale,
    },
  };
}

const PreguntasFrecuentes: NextPage = (props: any) => {
  const translation = useTranslations("faq");
  const faqTitle = translation("title") || "";
  const title = faqTitle;
  const introTitle = translation("introTitle");
  const introBody = translation("introBody");

  const questions =
    CFrequencyQuestions[props.lang as keyof typeof CFrequencyQuestions];
  const router = useRouter();
  const metaData: IHeadMetaComponent = {
    title: "frequentQuestions",
    description: "frequentQuestions",
    url: router.asPath,
    og_type: "page-frequentQuestions",
  };
  return (
    <Container container>
      <HeadMeta {...metaData}></HeadMeta>
      <CoverImage />
      <Content>
        <BorderForm>
          <Grid item xs={12}>
            <WsSectionTitle title={title} />
          </Grid>
        </BorderForm>

        <Grid container item xs={12} xl={8} lg={8} md={10} mb={{ xs: 4, md: 0 }} mx="auto">
          {(introTitle || introBody) && (
            <Grid item xs={12}>
              <Description>
                {introTitle && <p className="intro-title">{introTitle}</p>}
                {introBody && <p className="intro-body">{introBody}</p>}
              </Description>
            </Grid>
          )}
          {questions.map((x: any, i: number) => (
            <Grid item xs={12} key={i}>
              <CustomAccordion {...x}></CustomAccordion>
            </Grid>
          ))}
        </Grid>
      </Content>
    </Container>
  );
};

export default PreguntasFrecuentes;

interface CustomAccordionInterface {
  question: string;
  answer: string;
  key: number;
}

const CustomAccordion = (props: CustomAccordionInterface) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };
  return (
    <CustomRoot>
      <Accordion expanded={expanded} onChange={handleChange}>
        <AccordionSummary
          expandIcon={
            !expanded ? (
              <AddIcon color="primary" />
            ) : (
              <RemoveIcon color="primary" />
            )
          }
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="faq-clicked"
        >
          <Typography className="question-text">{props.question}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography className="answer-text">{props.answer}</Typography>
        </AccordionDetails>
      </Accordion>
    </CustomRoot>
  );
};

const CoverImage = styled.div`
  background-image: url("/images/v2/pic/image-07.webp");
  background-size: cover;
  background-position: center 50%;
  background-repeat: no-repeat;
  width: 100%;
  height: 80vh;

  @media screen and (max-width: 500px) {
    height: 45dvh;
    background-position: center 10%;
    background-size: contain;
  }
  @media screen and (max-width: 500px) {
    height: 35dvh;
    background-position: center 10%;
    background-size: contain;
  }
`;

const Container = styled(Grid)`
  background-color: var(--color-brand-cream-primary);
  min-height: 100vh;
`;

const Content = styled(Grid)`
  background-color: var(--color-brand-cream-primary);
  position: relative;
  width: 100%;
  height: auto;
`;

const BorderForm = styled.div`
  position: absolute;
  top: -150px;
  left: 0;
  width: 100%;
  height: 150px;
  background-color: var(--color-brand-cream-primary);
  border-radius: 150px 0px 0px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: 500px) {
    top: -70px;
    height: 70px;
    border-radius: 0px;
  }
`;

const CustomRoot = styled.div`
  & .MuiPaper-root {
    background: transparent;
    box-shadow: none;
    padding: 5px 35px;
    &.Mui-expanded {
      background: white !important;
      border-radius: 20px;
      padding-bottom: 10px;
      margin: 10px 0px;

      &:last-of-type {
        margin-bottom: 10px;
      }
    }
  }
  & .question-text {
    color: black;
    font-size: var(--fs-sub-title-small);
    font-weight: 600;
  }
  & .answer-text {
    color: var(--color-brand-grey-main);
    font-size: var(--fs-sub-title-small);
    font-weight: 500;
  }
`;

const Description = styled.div`
  width: 92%;
  padding: 2rem 3.5rem;
  border-radius: 10px;
  margin: 0 auto 3rem;
  background: white;
  color: var(--color-brand-grey-main);
  font-size: var(--fs-sub-title-small);
  line-height: 1.3;
  border: 1px solid var(--color-brand-grey-light);
  .intro-title {
    color: var(--color-text-primary);
    font-weight: 700;
    margin: 0 0 8px;
  }
  .intro-body {
    margin: 0;
    white-space: pre-line;
  }
`;
